import { calculateOrderTotals } from '../utils/calcTotals';
import { ref, push, set, get, runTransaction, update } from 'firebase/database';
import { db } from '../../../integrations/firebase';

import { GoogleSheetService } from './GoogleSheetService';
import { sendOrderUpdate } from '../../integrations/services/InteraktService';
import { CustomerSyncService } from '../../../core/customer/CustomerSyncService';
import { dimsForSku } from '../data/productDimensions';
import { getShipmentMetrics } from '../utils/shipmentMetrics';
import { getAuth } from 'firebase/auth';
import { calculateShippingCost } from './ShippingService';
import { discountSummary, discountDecision, discountApprovalRecord } from '../logic/discountPolicy';
import { checkCodCampaign } from '../logic/codCampaignGuard';

// Pull a 6-digit pincode out of free-text address fields when the structured
// pincode is missing (common for website/parsed orders).
const pincodeFromAddress = (c = {}) => {
    for (const f of [c.pincode, c.postcode, c.zip]) {
        const s = String(f || '').trim();
        if (/^\d{6}$/.test(s)) return s;
    }
    for (const f of [c.address, c.fullAddress, c.city]) {
        const m = String(f || '').match(/\b\d{6}\b/);
        if (m) return m[0];
    }
    return '';
};

export const createOrder = async (orderData, productsMap) => {
    const { customer = {}, items, payment, shipping, shippingCost, agentName, source, courierPreference } = orderData;
    if (!customer.pincode || !/^\d{6}$/.test(String(customer.pincode))) {
        const derived = pincodeFromAddress(customer);
        if (derived) customer.pincode = derived;
    }
    // Still missing? Ask the website (real checkout pincode) by mobile, via the
    // server proxy (which holds the secret). Best-effort — never blocks the order.
    if ((!customer.pincode || !/^\d{6}$/.test(String(customer.pincode))) && customer.mobile) {
        try {
            const PROXY = (import.meta.env.VITE_PROXY_URL || (import.meta.env.DEV ? 'http://localhost:3001' : '')).replace(/\/$/, '');
            const { authHeaders } = await import('../../../core/api/authHeaders');
            const m10 = String(customer.mobile).replace(/\D/g, '').slice(-10);
            const r = await fetch(`${PROXY}/api/website/pincode?mobile=${m10}`, { headers: { ...(await authHeaders()) } });
            if (r.ok) {
                const d = await r.json();
                if (d && /^\d{6}$/.test(String(d.pincode || ''))) {
                    customer.pincode = String(d.pincode);
                    if (!customer.address && d.address) customer.address = d.address;
                    if (!customer.city && d.city) customer.city = d.city;
                    if (!customer.state && d.state) customer.state = d.state;
                }
            }
        } catch (e) { /* best effort */ }
    }

    try {
        // 1. Validate Stock & Prepare Items
        const finalItems = [];

        for (const item of items) {
            if (item.sku === 'PNC') {
                finalItems.push({
                    sku: 'PNC',
                    name: 'Product Not Confirmed',
                    qty: Number(item.qty),
                    price: 0,
                    priceBeforeTax: 0,
                    priceAfterTax: 0,
                    gstRate: 0,
                    hsn: '',
                    taxAmount: 0,
                    total: 0,
                    weight_grams: 0,
                    dimensions: {}
                });
                continue;
            }

            const product = productsMap[item.sku];
            if (!product) throw new Error(`Product not found: ${item.sku}`);
            // PRICES COME FROM THE PRODUCT LIST ONLY. There is no per-line price and
            // no "free" line any more: money off is payment.discount, which a manager
            // approves above ₹100. (DP07223 was saved with a ₹-796 product line
            // because "₹796 off" was typed into a per-line price box.)
            const basePrice = Number(product.priceAfterTax ?? product.priceMRP ?? 0);
            if (item.free) throw new Error(`"${product.name}" cannot be added free. Use the Discount box — a manager approves anything above ₹100.`);
            if (item.priceOverride != null && item.priceOverride !== '' && Number(item.priceOverride) !== basePrice) {
                throw new Error(`The price of "${product.name}" comes from the product list (₹${basePrice}) and cannot be changed here. Use the Discount box.`);
            }
            if (!(basePrice >= 0)) throw new Error(`"${product.name}" has a price below ₹0 in the product list — fix the product first.`);

            const qty = Number(item.qty);
            const stock = Number(product.initialStock || 0);

            if (stock < qty) {
                throw new Error(`Insufficient stock for "${product.name}". Available: ${stock}`);
            }

            const unitPrice = basePrice;
            // Stamp weight + box dimensions: product master first, then the manual
            // SKU map (scripts/product-dimensions-map.json mirror).
            const mapDims = dimsForSku(item.sku);
            // WEIGHT COMES FROM THE PRODUCT MASTER ONLY — whatever the Products screen
            // shows under "Weight (g)" is the weight, full stop. It writes `weight_grams`
            // for some items and `weight` for others (HP11 holds the string "1000"), so
            // both are read. The SKU map is NOT consulted for weight any more: its HP11
            // entry claims 5390 g against the master's 1000 g, and a stale file quietly
            // overruling the screen staff actually edit is how parcels got mispriced.
            // The map still supplies BOX DIMENSIONS, which the master does not always hold.
            const weightG = Number(product.weight_grams) || Number(product.weight) || 0;
            const itemEntry = {
                sku: item.sku,
                name: product.name,
                qty: qty,
                price: unitPrice,
                priceBeforeTax: Number(product.priceBeforeTax ?? 0),
                priceAfterTax: unitPrice,
                gstRate: Number(product.gstRate ?? 0),
                hsn: product.hsn || '', // Ensure HSN is passed
                taxAmount: Number(product.taxAmount ?? 0),
                total: unitPrice * qty,
                // The product's own price when the order was made, so any money off
                // on this line stays visible (and counts towards the approval limit).
                listPrice: basePrice,
                isFree: false,
                weight_grams: weightG,
                dimensions: product.dimensions || {},
            };
            if (mapDims) {
                itemEntry.boxLength = Number(product.boxLength) || mapDims.boxLength;
                itemEntry.boxBreadth = Number(product.boxBreadth) || mapDims.boxBreadth;
                itemEntry.boxHeight = Number(product.boxHeight) || mapDims.boxHeight;
                itemEntry.dimensionSource = 'manual_sku_map';
            }
            finalItems.push(itemEntry);
        }

        // Calculate Totals using Utility
        const totals = calculateOrderTotals(finalItems, shippingCost);
        // Manual order-level discount (₹) from the desk — clamped so a typo can
        // never push the total below the shipping charge or go negative.
        // Never negative, never silently clamped: a discount larger than the products
        // is refused, not quietly turned into a free order.
        const rawDiscount = Number(payment?.discount) || 0;
        if (rawDiscount < 0) throw new Error('The discount cannot be below ₹0.');
        if (rawDiscount > totals.subtotal + 0.005) {
            throw new Error(`The discount (₹${rawDiscount}) is more than the products (₹${totals.subtotal}). Mark the product Free instead, which a manager must approve.`);
        }
        const discount = rawDiscount;
        // THE COD FEE — decided HERE for every screen (owner's rule: COD logic is
        // central). It is its own charge (payment.cod_fee / codFee), never folded into
        // shipping, so "convert to prepaid" can take it off and the courier's COD amount
        // includes it. A caller may pass payment.codFee; otherwise it comes from the
        // shipping settings (Kerala COD ₹44), less any COD charge the caller already put
        // in shipping (the parser desk does), so it is never charged twice. The profile
        // Add Order window used to send ₹0 for everything — 14 COD orders were ₹44 short.
        const isCodMode = String(payment?.mode || '').toUpperCase().includes('COD');
        let codFee = 0;
        if (isCodMode) {
            if (payment?.codFee != null && payment.codFee !== '') {
                codFee = Math.max(0, Math.round(Number(payment.codFee) || 0));
            } else {
                try {
                    const st = customer?.state || 'Kerala';
                    const [codCharge, prepaidShip] = await Promise.all([calculateShippingCost(st, 'COD'), calculateShippingCost(st, 'Prepaid')]);
                    const feeRule = Math.max(0, Number(codCharge) - Number(prepaidShip));
                    const alreadyInShipping = Math.max(0, Number(totals.shipping || 0) - Number(prepaidShip));
                    codFee = Math.max(0, Math.round(feeRule - alreadyInShipping));
                } catch { codFee = 0; }
            }
        }
        const grandTotal = Math.max(0, Number((totals.total + codFee - discount).toFixed(2)));

        // "No COD" campaigns: the storefront refuses COD to members of a live campaign
        // with COD off. Here, for every screen, the desk is WARNED and must confirm; a
        // confirmed order carries codCampaignOverride. Before any write, so "Cancel"
        // leaves nothing behind. Callers may pass orderData.confirmCodCampaign.
        const codCampaign = await checkCodCampaign({
            mobile: customer?.mobile, isCod: isCodMode, agentName,
            confirm: typeof orderData.confirmCodCampaign === 'function' ? orderData.confirmCodCampaign
                : (typeof window !== 'undefined' && typeof window.confirm === 'function' ? (msg) => window.confirm(msg) : undefined),
            fetchCodOff: async (m10) => {
                const PROXY = (import.meta.env.VITE_PROXY_URL || (import.meta.env.DEV ? 'http://localhost:3001' : '')).replace(/\/$/, '');
                const { authHeaders } = await import('../../../core/api/authHeaders');
                const r = await fetch(`${PROXY}/api/campaigns/cod-off?mobile=${m10}`, { headers: { ...(await authHeaders()) } });
                if (!r.ok) throw new Error(`cod-off ${r.status}`);
                return r.json();
            },
        });
        if (!codCampaign.proceed) throw new Error(codCampaign.error);

        // More than ₹100 off in total (discount + lower prices + free lines) needs a
        // manager. A manager's own order is approved by them; anyone else's waits
        // and cannot be given to a courier until a manager approves it.
        const dSummary = discountSummary({
            lines: finalItems.filter(i => i.sku !== 'PNC').map(i => ({ name: i.name, listPrice: i.listPrice, unitPrice: i.price, qty: i.qty, free: i.isFree })),
            discount,
        });
        let creatorRole = '';
        try {
            const uid = getAuth().currentUser?.uid;
            if (uid) creatorRole = String((await get(ref(db, `users/${uid}/role`))).val() || '');
        } catch { /* no role → treated as not a manager: the order waits for approval */ }
        const dDecision = discountDecision(dSummary, creatorRole);
        if (dDecision.blocked) throw new Error(dDecision.blocked);
        const discountApproval = discountApprovalRecord(dSummary, { role: creatorRole, agentName });
        // Partial COD: a prepaid advance collected up front (via a payment link),
        // so the courier collects only the remainder. Only meaningful on COD
        // orders; clamped to [0, grandTotal] so a typo can't exceed the total.
        const isCod = String(payment?.mode || '').toUpperCase().includes('COD');
        const codAdvance = isCod
            ? Math.min(Math.max(0, Math.round(Number(payment?.codAdvance) || 0)), Math.round(grandTotal))
            : 0;

        // 2. Create Order Object
        const ordersRef = ref(db, 'orders');
        const newOrderRef = push(ordersRef);

        // Generate Custom Order ID (Sequential)
        const configSnapshot = await get(ref(db, 'settings/orderSeries'));
        let orderId = newOrderRef.key; // Fallback

        if (configSnapshot.exists()) {
            const config = configSnapshot.val();
            const { permanentPrefix, weeklyPrefix, sequence } = config;
            orderId = `${permanentPrefix}${weeklyPrefix}${sequence}`;

            // Increment Sequence
            await set(ref(db, 'settings/orderSeries/sequence'), sequence + 1);
        } else {
            // Initialize Series if missing
            const initialConfig = {
                permanentPrefix: 'ORD-',
                weeklyPrefix: 'GEN-',
                sequence: 1001
            };
            await set(ref(db, 'settings/orderSeries'), initialConfig);
            orderId = `ORD-GEN-1000`;
        }

        const newOrder = {
            id: orderId,
            source: source || 'app', // Track order origin (e.g. 'customer_profile')
            agentName: agentName || 'System', // Persist Agent Name
            createdAt: new Date().toISOString(),
            status: 'pending',
            customer: {
                name: customer.name,
                mobile: customer.mobile,
                alternateMobile: customer.alternateMobile || '', // Persist Alt Mobile
                address: customer.address,
                city: customer.city,
                state: customer.state,
                pincode: customer.pincode
            },
            items: finalItems,
            ...(discountApproval ? { discountApproval } : {}),
            ...(codCampaign.stamp ? { codCampaignOverride: codCampaign.stamp } : {}),
            package: (() => { const m = getShipmentMetrics({ items: finalItems }); return { weight: m.weightKg, weightGrams: m.weightGrams, length: m.boxLength, breadth: m.boxBreadth, height: m.boxHeight }; })(),
            payment: {
                mode: payment.mode,
                subtotal: totals.subtotal,
                ...(discount > 0 ? { discount } : {}),
                shipping: totals.shipping,
                ...(codFee > 0 ? { codFee, cod_fee: codFee } : {}),
                total: grandTotal,
                // Partial-COD advance (0 = full COD / prepaid). The advance starts
                // UNPAID; the desk collects it via a link and marks it paid, which
                // unlocks courier assignment (see dispatchReadiness).
                ...(codAdvance > 0 ? { codAdvance, codAdvancePaid: false } : {}),
                status: 'pending'
            },
            shipping: {
                ...shipping,
                courier: shipping.courier || 'Normal', // Persist Courier
                courierPreference: courierPreference || shipping.courierPreference || shipping.courier || 'Normal',
                batchNo: null,
                shippedDate: null,
                weight_grams: finalItems.reduce((sum, i) => sum + (i.weight_grams * i.qty), 0)
            }
        };

        // 3. Save Order
        await set(newOrderRef, newOrder);

        // 4. Deduct Stock
        for (const item of finalItems) {
            if (item.sku === 'PNC') continue; // Skip stock deduction for placeholder

            const productRef = ref(db, `products/${item.sku}`);
            await runTransaction(productRef, (product) => {
                if (!product) return product;
                // Deduct from both fields to ensure consistency across different UI components
                if (product.stock !== undefined) product.stock = Number(product.stock) - item.qty;
                if (product.initialStock !== undefined) product.initialStock = Number(product.initialStock) - item.qty;
                return product;
            });

            // Log Inventory
            const logRef = push(ref(db, `inventoryLogs/${item.sku}`));
            await set(logRef, {
                date: new Date().toISOString(),
                change: -item.qty,
                reason: `Order ${orderId}`,
                type: 'order_deduction'
            });
        }

        // 5. Update Customer Intelligence — routed through the centralized
        // CustomerSyncService (single source of truth; field-level, normalized
        // key, consistent analytics). Non-fatal: never block order creation.
        try {
            await CustomerSyncService.syncCustomerFromOrder(newOrder, { uid: agentName || 'system' });
        } catch (syncErr) {
            console.warn('[CustomerSync] order sync failed (non-fatal):', syncErr.message);
        }

        // 6. Sync to Google Sheets (Conditional)
        const isPrepaid = payment.mode === 'Prepaid';
        if (isPrepaid) {
            // Prepaid: Sync immediately & Set Processed (if not already set above)
            // Note: We set 'pending' above. Let's update it or rely on the UI to see 'pending' as usable?
            // User said: "FOR PREPAID ORDER... DIRECTLY STATUS TO PROCESSED"
            // So we should have initialized it as 'Processed'. 
            // Since we already saved it as 'pending', let's update it here or change initialization.
            // Better: update initialization. But I can't easily change lines 89-118 with this tool without large context.
            // So I'll do a quick update if prepaid. 
            // Actually, let's just sync here. I'll modify the initial object creation in a separate edit if needed, 
            // or just assume 'pending' is fine for now and we sync.
            // Wait, user explicitly said "STATUS NEED TO CHANGE TO PROCESSED".
            update(newOrderRef, { status: 'Processed' });
            newOrder.status = 'Processed'; // Update local for sync

            GoogleSheetService.syncOrder(newOrder).catch(err => console.error("Background Sheet Sync Failed:", err));
        } else {
            console.log("COD Order: Skipping Sheet Sync until confirmed.");
        }

        // 7. Send Interakt WhatsApp Message (Non-blocking)
        // Fetch Settings first
        get(ref(db, 'settings/integrations')).then(snap => {
            if (snap.exists()) {
                const config = snap.val();
                sendOrderUpdate(newOrder, config).then(res => {
                    console.log("[Interakt] Trigger Result:", res);
                });
            }
        }).catch(err => console.warn("[Interakt] Config fetch failed:", err));

        return { success: true, orderId, order: newOrder, firebaseKey: newOrderRef.key, ...(codCampaign.warning ? { warning: codCampaign.warning } : {}) };

    } catch (error) {
        console.error("Order Creation Failed:", error);
        return { success: false, error: error.message };
    }
};
